import { useToast } from "@/app/hooks/toast/useToast";
import { useBreakpoint } from "@/app/hooks/useBreakpoint";
import { setFlightSearchRequestToLocalStorage } from "@/app/utils/services/service/localStorageService";
import type { FlightSearchRequest, SearchValues } from "@/types/flights";
import axios from "axios";
import clsx from "clsx";
import { size } from "lodash";
import { Search, X } from "lucide-react";
import { useRouter } from "@/i18n/navigation";
import { useState, type RefObject } from "react";
import Button from "../../Button/Button";
import DestinationField from "../DestinationField/DestinationField";
import CustomDatePicker from "./FlightDatePicker";
import { useLocale } from "next-intl";
import { useTranslations } from "use-intl";

type Props = {
  values: SearchValues;
  onAddDestination: () => void;
  onRemoveDestination: (id: string) => void;
  onClearAll: () => void;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  buildFlightSearchPayload: () => string;
};

export const FlightFields = ({
  onRemoveDestination,
  onAddDestination,
  onClearAll,
  values,
  updateFlightField,
  buildFlightSearchPayload,
}: Props) => {
  const areAtLeastThreeFlights = size(values.flights) > 2;
  const isMultiCity = values.flightType === "multi_city";
  const breakpoint = useBreakpoint();
  const locale = useLocale();
  const t = useTranslations();
  const isSmallScreen = !breakpoint || breakpoint === "sm";

  const { push } = useRouter();
  const { showToast } = useToast();
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(false);

  const searchForFlights = async () => {
    try {
      setSearchLoading(true);
      const payload = buildFlightSearchPayload();
      setFlightSearchRequestToLocalStorage(JSON.stringify(values), locale);
      const { data } = await axios.post(
        `/api/flights/search?lang=${locale}`,
        payload
      );
      push(`/flights/results?searchId=${data?.searchId}`);
      setSearchLoading(false);
    } catch (err: any) {
      setSearchLoading(false);
      if (err?.response?.data?.error) {
        showToast(err.response.data.error, "error");
      } else if (err?.response?.statusText) {
        showToast(err.response.statusText, "error");
      } else {
        showToast("There are no flights available.", "error");
      }
    }
  };

  return (
    <div className={clsx("flex gap-2 flex-col", !isMultiCity && "md:flex-row")}>
      {values?.flights?.map((flight, index) => (
        <div key={flight.id} className="flex-grow">
          {isMultiCity && isSmallScreen && (
            <div className="flex justify-between pt-2.5 pb-1.5">
              <h3 className="font-inter text-base text-sleap-gray-950">
                {t("HomePageFlights.tripOrder", { order: index + 1 })}
              </h3>
              {areAtLeastThreeFlights && (
                <button
                  type="button"
                  onClick={() => onRemoveDestination(flight.id)}
                  className="flex items-center text-base text-sleap-gray-700 font-inter gap-1.5 px-1.5"
                >
                  <X /> {t("Other.remove")}
                </button>
              )}
            </div>
          )}
          <div className="flex gap-2 flex-col md:flex-row">
            <div className="md:w-1/2">
              <DestinationField
                from={flight.from}
                to={flight.to}
                flightId={flight.id}
                updateFlightField={updateFlightField}
                withSwitch={!isSmallScreen && !areAtLeastThreeFlights}
                index={index}
              />
            </div>
            <div className="md:w-1/2">
              <CustomDatePicker
                departOnly={values.flightType !== "rounded"}
                departDate={flight.departDate}
                returnDate={flight.returnDate}
                flightId={flight.id}
                updateFlightField={updateFlightField}
              />
            </div>{" "}
            {!isSmallScreen && areAtLeastThreeFlights && (
              <button
                className="block bg-sleap-gray-200 hover:bg-sleap-gray-400 text-sleap-gray-950 rounded-lg p-4"
                onClick={() => onRemoveDestination(flight.id)}
              >
                <X />
              </button>
            )}
          </div>
        </div>
      ))}
      <div
        className={clsx(
          "flex flex-col gap-2 md:flex-row md:justify-between",
          isMultiCity && "pt-1"
        )}
      >
        {isMultiCity && (
          <div className="flex gap-2 w-full md:w-auto">
            {values.flights.length < 4 && (
              <Button variant="neutral" onClick={onAddDestination} fullWidth>
                {t("HomePageFlights.addDestination")}
              </Button>
            )}
            {!isSmallScreen && (
              <Button variant="white" onClick={onClearAll}>
                {t("Other.clearAll")}
              </Button>
            )}
          </div>
        )}
        <Button
          onClick={searchForFlights}
          isLoading={searchLoading}
          disabled={
            searchLoading ||
            error ||
            !values?.flights?.every(
              (flight) => flight.from && flight.to && flight.departDate
            )
          }
        >
          <Search /> {t("Other.search")}
        </Button>
      </div>
    </div>
  );
};
